import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { authAPI } from '../services/api';
import {
  clearEmployeeSessionFromWindowsAgent,
  syncTokenToWindowsAgent,
} from '../utils/agentLocalAuth';
import { generateDeviceId } from '../utils/deviceId';
import {
  AUTH_TOKEN_KEY,
  AUTH_USER_KEY,
  REMEMBERED_EMAIL_KEY,
  clearApplicationAuthStorage,
} from '../utils/authStorage';

export { clearApplicationAuthStorage } from '../utils/authStorage';

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => void;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);
      const storedUser = localStorage.getItem(AUTH_USER_KEY);

      if (storedToken && storedUser) {
        try {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));

          const response = await authAPI.verify();
          if (response.data.success) {
            setUser(response.data.user);
            localStorage.setItem(AUTH_USER_KEY, JSON.stringify(response.data.user));
            void syncTokenToWindowsAgent(storedToken);
          } else {
            clearApplicationAuthStorage();
            setToken(null);
            setUser(null);
          }
        } catch (error) {
          console.error('Token verification failed:', error);
          clearApplicationAuthStorage();
          setToken(null);
          setUser(null);
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; message?: string }> => {
    try {
      const deviceId = generateDeviceId();

      const response = await authAPI.login({ email, password, deviceId });

      if (response.data.success && response.data.token && response.data.user) {
        setToken(response.data.token);
        setUser(response.data.user);
        localStorage.setItem(AUTH_TOKEN_KEY, response.data.token);
        localStorage.setItem(AUTH_USER_KEY, JSON.stringify(response.data.user));

        // Never persist email on shared workstations; dedicated may keep username UX via Chrome only.
        try {
          const policy = await authAPI.getLoginDevicePolicy(deviceId);
          const shared = Boolean(policy.data?.data?.sharedWorkstation);
          if (shared) {
            localStorage.removeItem(REMEMBERED_EMAIL_KEY);
            sessionStorage.removeItem(REMEMBERED_EMAIL_KEY);
          }
        } catch {
          // Policy lookup failure must not block login.
        }

        void syncTokenToWindowsAgent(response.data.token);
        return { success: true };
      }
      return { success: false, message: response.data.message || 'Login failed' };
    } catch (error: any) {
      console.error('Login failed:', error);
      const errorMessage = error.response?.data?.message || 'Login failed. Please try again.';
      return { success: false, message: errorMessage };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    clearApplicationAuthStorage();
    authAPI.logout().catch(console.error);

    void (async () => {
      let clearRememberedIdentity = false;
      try {
        const deviceId = generateDeviceId();
        const policy = await authAPI.getLoginDevicePolicy(deviceId);
        clearRememberedIdentity = Boolean(policy.data?.data?.sharedWorkstation);
      } catch {
        clearRememberedIdentity = false;
      }
      // Clears employee JWT only — never device registration / monitoring.
      void clearEmployeeSessionFromWindowsAgent({ clearRememberedIdentity });
    })();
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    isLoading,
    isAuthenticated: !!user && !!token,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
